/**
 * ModeSpine — the Planning/Shopping segmented control (T12.1.5.1.1).
 *
 * Lives INSIDE the header row, between the brand and the right-side controls
 * (matching the prototype: `brand · seg(Planning/Shopping) · ☰`). The old
 * full-width band below the header — plus its GPS soft-default hint chip and the
 * collapsed `📍` marker — are gone; the prototype dropped that wasteful row. GPS
 * detection runs automatically (native: on entry; web: silently when granted),
 * and the persistent LocationNeededBanner (T12.6 D6, home-coords gate T12.6.1)
 * — not a header button — is the ambient opt-in affordance when location isn't
 * working AND the household has no home coordinates.
 *
 * An animated thumb slides between the two segments; each segment is a ≥44px
 * thumb-zone target.
 *
 * Reads + writes the mode store via the plain `useSetAppMode` — a switch is a
 * temporary in-memory deviation, nothing persisted and no override flag. A
 * re-launch or a logo press recomputes the mode from GPS (T12.6 Reopen 2).
 * Part of the one responsive mobile-first shell — renders at every width.
 *
 * Post-manual-switch location hint (T12.6.1). For a household that HAS home
 * coordinates (so the ambient banner stays hidden, per its gate) but whose GPS
 * isn't working, a MANUAL tap to Shopping is the moment the value of turning
 * GPS on is concrete — the in-store auto-switch would otherwise do this for
 * free. A dismissible inline hint appears under the switcher then (never for a
 * GPS-driven switch, which needs no ask — it already worked); dismissal is
 * device-local with a 14-day re-surface (`stores/dismissal.ts`). AppShell is
 * the sole `useModeDetection` caller and passes `locationWorking` /
 * `hasHomeCoords` / `requestDetection` down as props (same values the banner
 * consumes) — this component has no detection side effect of its own.
 *
 * Tokens-only (CP5). No hex; hairline = 0.0625rem.
 */

import { useState, type CSSProperties } from 'react';
import type { AppMode } from '@shopista/shared/stores';
import { useAppMode, useSetAppMode } from '../stores/useAppMode';
import { dismissSurface, isSurfaceDismissed } from '../stores/dismissal';
import { Icon } from '../components';

// T12.6.1 — the dismissal surface key for the post-manual-switch hint.
const LOCATION_HINT_SURFACE = 'mode-switch-location-hint';

// The outer flex slot ModeSpine occupies in the header row (brand · seg · ☰):
// takes the slack between the brand and the right-side controls, capped so it
// stays a control (not a band) on wide screens — prototype: flex:1, max
// ~220px. `position: relative` anchors the T12.6.1 hint popover below it.
const spineWrapStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flex: 1,
  maxWidth: '13.75rem',
  margin: '0 var(--space-2)',
};

// The compact segmented pill itself — layout-positioning (flex/maxWidth/margin)
// now lives on `spineWrapStyle` above so the T12.6.1 hint can anchor to the
// wrapper without stretching the pill's own box.
const segStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flex: 1,
  backgroundColor: 'var(--color-bg-muted)',
  borderRadius: 'var(--radius-pill)',
  padding: 'var(--space-1)',
};

const thumbBase: CSSProperties = {
  position: 'absolute',
  top: 'var(--space-1)',
  bottom: 'var(--space-1)',
  left: 'var(--space-1)',
  width: 'calc(50% - var(--space-1))',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-accent-primary)',
  transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 1,
};

const buttonBase: CSSProperties = {
  flex: 1,
  zIndex: 2,
  minWidth: 0,
  minHeight: '2.75rem', // 44px thumb target
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-1)',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  borderRadius: 'var(--radius-pill)',
  whiteSpace: 'nowrap',
};

const buttonInactive: CSSProperties = {
  ...buttonBase,
  color: 'var(--color-text-secondary)',
};

const buttonActive: CSSProperties = {
  ...buttonBase,
  color: 'var(--color-text-inverse)',
};

// T12.6.1 — the post-manual-switch location hint. Anchored below the
// segmented pill via `spineWrapStyle`'s `position: relative`; the same layer
// LocationNeededBanner uses (`--z-banner`) since it plays the same ambient
// role, just contextual instead of app-wide.
const hintStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 'var(--space-1)',
  zIndex: 'var(--z-banner)' as unknown as number,
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-accent-secondary)',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-accent-subtle)',
  boxShadow: 'var(--shadow-md)',
};

const hintTextStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 'var(--font-size-3xs)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.35,
};

const hintEnableStyle: CSSProperties = {
  flex: '0 0 auto',
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-3)',
  fontSize: 'var(--font-size-3xs)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  whiteSpace: 'nowrap',
};

const hintDismissStyle: CSSProperties = {
  flex: '0 0 auto',
  minWidth: '2.75rem',
  minHeight: '2.75rem',
  border: 0,
  background: 'transparent',
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-sm)',
  lineHeight: 1,
  cursor: 'pointer',
  borderRadius: 'var(--radius-pill)',
  display: 'grid',
  placeItems: 'center',
};

export interface ModeSpineProps {
  /**
   * `useModeDetection().locationWorking`, forwarded by AppShell (T12.6.1).
   * Defaults to `null` (undetermined) so ModeSpine stays mountable standalone
   * (its own unit tests render it with no props).
   */
  locationWorking?: boolean | null;
  /**
   * `useModeDetection().hasHomeCoords`, forwarded by AppShell (T12.6.1) — the
   * hint only applies to a household that already has a home-coords fallback
   * (a household with none sees the ambient banner instead).
   */
  hasHomeCoords?: boolean;
  /** `useModeDetection().requestDetection`, forwarded by AppShell (T12.6.1). */
  requestDetection?: () => void;
}

export function ModeSpine({
  locationWorking = null,
  hasHomeCoords = false,
  requestDetection = () => {
    /* no-op default so standalone renders never throw */
  },
}: ModeSpineProps = {}): JSX.Element {
  const mode = useAppMode();
  const setMode = useSetAppMode();
  // T12.6.1 — tracks a MANUAL tap to Shopping (via this control) specifically,
  // as opposed to a GPS-driven mode change (which writes the store directly
  // from useModeDetection, bypassing this handler) — only the manual path
  // should ever surface the hint.
  const [manualShoppingSwitch, setManualShoppingSwitch] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(() =>
    isSurfaceDismissed(LOCATION_HINT_SURFACE),
  );

  const select = (next: AppMode) => {
    if (next !== mode) {
      setMode(next);
      setManualShoppingSwitch(next === 'shopping');
    }
  };

  function dismissHint(): void {
    dismissSurface(LOCATION_HINT_SURFACE);
    setHintDismissed(true);
  }

  // Show only right after a MANUAL switch to Shopping, while GPS itself isn't
  // working but the household has a home-coords fallback (the ambient banner
  // stays hidden for that household, per its own gate) — and not dismissed
  // within the last 14 days.
  const showLocationHint =
    mode === 'shopping' &&
    manualShoppingSwitch &&
    locationWorking === false &&
    hasHomeCoords &&
    !hintDismissed;

  return (
    <div style={spineWrapStyle} data-testid="mode-spine-wrap">
      <div
        role="group"
        aria-label="App mode"
        style={segStyle}
        data-mode={mode}
        data-testid="mode-spine-seg"
      >
        <span
          aria-hidden="true"
          style={{
            ...thumbBase,
            transform: mode === 'shopping' ? 'translateX(100%)' : 'translateX(0)',
          }}
        />
        <button
          type="button"
          style={mode === 'planning' ? buttonActive : buttonInactive}
          onClick={() => select('planning')}
          aria-pressed={mode === 'planning'}
          data-testid="mode-spine-planning"
        >
          <Icon name="compass" data-testid="mode-spine-planning-icon" /> Planning
        </button>
        <button
          type="button"
          style={mode === 'shopping' ? buttonActive : buttonInactive}
          onClick={() => select('shopping')}
          aria-pressed={mode === 'shopping'}
          data-testid="mode-spine-shopping"
        >
          <Icon name="cart" data-testid="mode-spine-shopping-icon" /> Shopping
        </button>
      </div>

      {showLocationHint && (
        <div
          style={hintStyle}
          role="status"
          data-testid="mode-spine-location-hint"
        >
          <span style={hintTextStyle}>
            Turn on location and Shopista switches to Shopping automatically at
            your store.
          </span>
          <button
            type="button"
            style={hintEnableStyle}
            onClick={requestDetection}
            data-testid="mode-spine-location-hint-enable"
          >
            Turn on
          </button>
          <button
            type="button"
            style={hintDismissStyle}
            onClick={dismissHint}
            aria-label="Dismiss"
            data-testid="mode-spine-location-hint-dismiss"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
